/**
 * <photo-frame-customizer> — one framed photo shown at true scale on a wall.
 *
 * The size and frame colour come from the product's own variant picker. The
 * orientation toggle drives the preview and is written to the line item as
 * ``properties[Orientation]`` so fulfilment receives it in the order manifest.
 *
 * Reuses the gallery-wall frame component (assets/gallery-wall.css). Sizes
 * (art + mould → outer) live in assets/photo-frame-sizes.json.
 *
 * Attributes:
 *   data-sizes             url of photo-frame-sizes.json
 *   data-upload-endpoint   uploads worker base url (blank = preview only)
 *   data-enable-properties "true" to mirror the filename when preview-only
 */
/**
 * The add-to-cart form. A product page carries several `form[action*="/cart/add"]`
 * elements (Shop Pay installments, etc.); only the one with the add button
 * actually submits, so line-item properties must be written there.
 */
function cartForm() {
  const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
  return forms.find((f) => f.querySelector('button[name="add"]')) || forms[0] || null;
}

if (!customElements.get('photo-frame-customizer')) {
  const SHEENS = {
    dark: 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 38%, rgba(0,0,0,0.28) 100%)',
    light: 'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(0,0,0,0.04) 55%, rgba(0,0,0,0.10) 100%)',
    none: 'none',
  };

  customElements.define(
    'photo-frame-customizer',
    class PhotoFrameCustomizer extends HTMLElement {
      connectedCallback() {
        this.stage = this.querySelector('[data-stage]');
        this.wall = this.querySelector('[data-wall]');
        this.orientationEl = this.querySelector('[data-orientation]');
        this.matEl = this.querySelector('[data-mat]');
        this.meta = this.querySelector('[data-meta]');
        this.qualityEl = this.querySelector('[data-quality]');
        this.roomPicker = this.querySelector('[data-room-picker]');
        this.fileInput = this.querySelector('[data-file-input]');
        this.endpoint = (this.dataset.uploadEndpoint || '').replace(/\/$/, '');
        this.photo = null; // { file, preview, status, url, error }
        this.size = null;
        this.color = null;
        this.orientation = 'portrait';
        this.mat = false;
        this.wallKey = 'wall'; // the plain wall, offered alongside the room photos
        this.view = 'wall'; // 'wall' (default) | 'room'
        this.offset = { x: 0, y: 0 }; // preview-only drag offset, % of the room
        this.roomBase = null;
        this.dragState = null;
        this.lastDragEnd = 0;

        this.fileInput.addEventListener('change', () => this.setPhoto(this.fileInput.files[0]));
        this.noteEl = this.querySelector('[data-note]');
        this.noteEl?.addEventListener('input', () => this.syncNote());

        for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
          this.stage?.addEventListener(type, (event) => this.onPointer(type, event));
        }

        this.addEventListener('click', (event) => {
          if (Date.now() - this.lastDragEnd < 350) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          const reset = event.target.closest('[data-reset-position]');
          if (reset) {
            this.resetPosition();
            return;
          }
          const clear = event.target.closest('[data-clear]');
          if (clear) {
            event.preventDefault();
            this.clearPhoto();
            return;
          }
          const upload = event.target.closest('[data-upload]');
          if (upload) this.pick();
          const orientation = event.target.closest('[data-orientation-value]');
          if (orientation) this.setOrientation(orientation.dataset.orientationValue);
          const mat = event.target.closest('[data-mat-value]');
          if (mat) this.setMat(mat.dataset.matValue);
          const room = event.target.closest('[data-room-value]');
          if (room) this.setRoom(room.dataset.roomValue);
        });

        this.addEventListener('dragover', (event) => {
          const zone = event.target.closest('[data-dropzone]');
          if (!zone) return;
          event.preventDefault();
          zone.classList.add('is-dragover');
        });
        this.addEventListener('dragleave', (event) => {
          event.target.closest('[data-dropzone]')?.classList.remove('is-dragover');
        });
        this.addEventListener('drop', (event) => {
          const zone = event.target.closest('[data-dropzone]');
          if (!zone) return;
          event.preventDefault();
          zone.classList.remove('is-dragover');
          const file = event.dataTransfer?.files?.[0];
          if (file && file.type.startsWith('image/')) this.setPhoto(file);
        });

        this.load();
        this.syncOrientation();
      }

      async load() {
        try {
          const [sizes, rooms] = await Promise.all([
            fetch(this.dataset.sizes).then((r) => r.json()),
            fetch(this.dataset.rooms).then((r) => r.json()),
          ]);
          this.sizes = sizes;
          this.rooms = rooms;
          this.roomName = this.dataset.room;
          this.room = rooms[this.roomName];
        } catch (error) {
          console.error('photo-frame-customizer: could not load data', error);
        }
        if (!this.sizes) {
          this.wall.textContent = 'Could not load sizes';
          return;
        }
        this.syncFromVariant();
        this.syncOrientation();
        this.gateSubmit();
      }

      /* ---- variant sync (the product's own Size / Color options) ---------- */

      sizeRadios() {
        return [...document.querySelectorAll('input[type="radio"][name^="Size"]')];
      }
      colorRadios() {
        return [...document.querySelectorAll('input[type="radio"][name^="Color"]')];
      }

      syncFromVariant() {
        const size = this.sizeRadios().find((r) => r.checked);
        const color = this.colorRadios().find((r) => r.checked);
        this.size = (size && size.value) || this.sizes.sizes[0].label;
        this.color = (color && color.value) || 'Black';
        this.render();
      }

      sizeDef() {
        return this.sizes.sizes.find((s) => s.label === this.size) || this.sizes.sizes[0];
      }
      colorDef() {
        return this.sizes.colors[this.color] || this.sizes.colors.Black;
      }

      /** Mat thickness (inches) — scales a little with the frame. */
      matThickness(art) {
        return Math.min(2.5, Math.max(1, Math.min(art.w, art.h) * 0.12));
      }

      /**
       * Outer frame size for the current size + orientation. The frame keeps the
       * product's size; a mat eats into the image rather than growing the frame.
       */
      outer() {
        const s = this.sizeDef();
        const art =
          this.orientation === 'landscape' ? { w: s.art.h, h: s.art.w } : { w: s.art.w, h: s.art.h };
        const mat = this.mat ? this.matThickness(art) : 0;
        return {
          w: art.w + 2 * s.mould,
          h: art.h + 2 * s.mould,
          mould: s.mould,
          mat,
          art,
          image: { w: Math.max(1, art.w - 2 * mat), h: Math.max(1, art.h - 2 * mat) },
        };
      }

      /* ---- render -------------------------------------------------------- */

      render() {
        this.renderFrame();
        this.renderMeta();
        this.renderToggles();
        this.renderRoomPicker();
        // The frame itself is the default view, set by data-default-view; a room
        // photo is the alternative. The view has to be set before placing,
        // because placing is room-only.
        this.setView(this.dataset.defaultView || 'wall');
        this.updateQualityWarning();
      }

      /**
       * One row of choices: each room photo, then the plain wall. The wall is
       * what the customizer used to show behind a separate "On the wall" tab.
       */
      renderRoomPicker() {
        if (!this.roomPicker || !this.rooms) return;
        const options = Object.keys(this.rooms)
          .filter((key) => !key.startsWith('_'))
          .map((key) => ({ key, label: this.rooms[key].label || key }));
        options.push({ key: this.wallKey, label: 'Wall' });

        this.roomPicker.innerHTML = options
          .map(
            ({ key, label }) => `<button type="button" class="photo-frame-customizer__room"
              data-room-value="${key}">${label}</button>`
          )
          .join('');
        this.updateRoomPicker();
      }

      updateRoomPicker() {
        if (!this.roomPicker) return;
        this.roomPicker.querySelectorAll('[data-room-value]').forEach((button) => {
          const value = button.dataset.roomValue;
          const active =
            value === this.wallKey ? this.view !== 'room' : this.view === 'room' && value === this.roomName;
          button.classList.toggle('is-active', active);
        });
      }

      setRoom(key) {
        if (key === this.wallKey) {
          this.setView('wall');
          return;
        }
        if (!this.rooms || !this.rooms[key]) return;
        this.roomName = key;
        this.room = this.rooms[key];
        this.offset = { x: 0, y: 0 };
        this.setView('room');
      }

      /** Switch between the room photos and the plain wall. */
      setView(view) {
        this.view = view;
        const isRoom = view === 'room';
        this.stage?.classList.toggle('is-room', isRoom);
        this.classList.toggle('is-room-view', isRoom);
        this.updateRoomPicker();
        if (isRoom) this.place();
      }

      renderToggles() {
        this.orientationEl?.querySelectorAll('[data-orientation-value]').forEach((button) => {
          button.classList.toggle('is-active', button.dataset.orientationValue === this.orientation);
        });
        this.matEl?.querySelectorAll('[data-mat-value]').forEach((button) => {
          button.classList.toggle('is-active', (button.dataset.matValue === 'mat') === this.mat);
        });
      }

      setOrientation(value) {
        this.orientation = value === 'landscape' ? 'landscape' : 'portrait';
        this.render();
        this.syncOrientation();
      }

      setMat(value) {
        this.mat = value === 'mat';
        this.render();
      }

      renderFrame() {
        const { w, h, mould, mat } = this.outer();
        const color = this.colorDef();
        const onlyPrint = !color.frame;
        // the matboard reveal scales with the frame, it isn't a fixed width
        const matboard = mat ? +(w * 0.015).toFixed(3) : 0;

        this.wall.style.setProperty('--gw-w', w);
        this.wall.style.setProperty('--gw-h', h);
        this.wall.style.setProperty('--frame-border', onlyPrint ? 0 : mould);
        this.wall.style.setProperty('--mat', mat);
        this.wall.style.setProperty('--matboard', matboard);
        this.wall.style.setProperty('--frame-color', color.frame || 'transparent');
        this.wall.style.setProperty('--mat-color', mat ? '#fbfaf7' : color.frame || 'transparent');
        this.wall.style.setProperty('--frame-sheen', SHEENS[color.sheen] || 'none');
        this.wall.classList.toggle('is-no-mat', !mat);

        this.wall.innerHTML = `
          <figure class="frame" style="--x:0; --y:0; --w:${w}; --h:${h}; --mat:0">
            <div class="frame__mat"><div class="frame__art" data-art="0"></div></div>
            <button type="button" class="photo-frame-customizer__hit" data-upload
              aria-label="Choose a photo">
              <span class="photo-frame-customizer__plus" aria-hidden="true">+</span>
            </button>
          </figure>`;

        this.applyPhoto();
        this.place();
      }

      renderMeta() {
        const { art, mat, image } = this.outer();
        const onlyPrint = !this.colorDef().frame;
        const parts = [this.size, `${art.w} × ${art.h} in`];
        if (mat) parts.push(`image ${+image.w.toFixed(1)} × ${+image.h.toFixed(1)} in`);
        parts.push(onlyPrint ? 'print only' : `${this.color} frame`);
        this.meta.textContent = parts.join(' · ');
      }

      applyPhoto() {
        const art = this.wall.querySelector('[data-art="0"]');
        if (!art || !this.photo) return;
        art.style.backgroundImage = `url('${this.photo.preview}')`;
        art.classList.add('is-filled');
        this.wall.querySelector('.frame')?.classList.add('is-filled');
      }

      /**
       * Place the frame in the room photo at true scale — same maths as the
       * gallery-wall generator: one reference measurement sets px-per-inch, the
       * frame is centred on anchor.centerX, and its bottom sits `gap` inches
       * above the sofa back.
       */
      place() {
        const room = this.room;
        if (!room) return;

        const { w, h } = this.outer();
        const ppi = room.reference.px / room.reference.inches;
        const wallPxW = w * ppi;
        const wallPxH = h * ppi;
        const left = room.anchor.centerX - wallPxW / 2;
        const topMarginPx = (room.topMargin ?? 6) * ppi;
        const gapPx = (room.gap ?? 8) * ppi;

        let top = room.anchor.sofaBackTopY - gapPx - wallPxH;
        if (top < topMarginPx) top = topMarginPx;

        this.stage.style.setProperty('--room-image', `url('${room.image}')`);
        this.stage.style.setProperty('--room-ratio', `${room.size.w} / ${room.size.h}`);
        this.stage.style.setProperty('--room-width', `${((wallPxW / room.size.w) * 100).toFixed(3)}%`);

        this.roomBase = {
          leftPct: (left / room.size.w) * 100,
          topPct: (top / room.size.h) * 100,
          widthPct: (wallPxW / room.size.w) * 100,
          heightPct: (wallPxH / room.size.h) * 100,
        };
        this.applyRoomPlacement();
      }

      /** Base placement + the preview drag offset, clamped to the room photo. */
      applyRoomPlacement() {
        const b = this.roomBase;
        if (!b) return;
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        const x = clamp(b.leftPct + this.offset.x, 0, 100 - b.widthPct);
        const y = clamp(b.topPct + this.offset.y, 0, 100 - b.heightPct);
        this.stage.style.setProperty('--room-left', `${x.toFixed(3)}%`);
        this.stage.style.setProperty('--room-top', `${y.toFixed(3)}%`);
        this.querySelector('[data-reset-position]')?.toggleAttribute('hidden', this.offset.x === 0 && this.offset.y === 0);
      }

      /* ---- preview-only drag to reposition the frame ---------------------- */

      onPointer(type, event) {
        if (type === 'pointerdown') return this.dragStart(event);
        if (type === 'pointermove') return this.dragMove(event);
        return this.dragEnd(event);
      }

        dragStart(event) {
          if (event.button !== 0 || this.view !== 'room' || !this.roomBase) return;
        const rect = this.stage.getBoundingClientRect();
        this.dragState = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          rectW: rect.width,
          rectH: rect.height,
          startOffset: { ...this.offset },
          moved: false,
        };
      }

      dragMove(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        const dx = event.clientX - d.startX;
        const dy = event.clientY - d.startY;
        if (!d.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;

        if (!d.moved) {
          d.moved = true;
          this.stage.setPointerCapture?.(event.pointerId);
        }
        this.stage.classList.add('is-dragging');
        event.preventDefault();

        const b = this.roomBase;
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        this.offset.x = clamp(
          d.startOffset.x + (dx / d.rectW) * 100,
          -b.leftPct,
          100 - b.widthPct - b.leftPct
        );
        this.offset.y = clamp(
          d.startOffset.y + (dy / d.rectH) * 100,
          -b.topPct,
          100 - b.heightPct - b.topPct
        );
        this.applyRoomPlacement();
      }

      dragEnd(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        this.dragState = null;
        this.stage.classList.remove('is-dragging');
        if (d.moved) this.lastDragEnd = Date.now();
      }

      resetPosition() {
        this.offset = { x: 0, y: 0 };
        this.applyRoomPlacement();
      }

      /* ---- photo + upload ------------------------------------------------ */

      pick() {
        this.fileInput.value = '';
        this.fileInput.click();
      }

      setPhoto(file) {
        if (!file) return;
        if (this.photo) URL.revokeObjectURL(this.photo.preview);
        const preview = URL.createObjectURL(file);
        this.photo = {
          file,
          preview,
          url: '',
          status: this.endpoint ? 'uploading' : 'ready',
          width: 0,
          height: 0,
        };
        const image = new Image();
        image.onload = () => {
          if (!this.photo || this.photo.preview !== preview) return;
          this.photo.width = image.naturalWidth;
          this.photo.height = image.naturalHeight;
          this.updateQualityWarning();
        };
        image.src = preview;

        this.applyPhoto();
        const zone = this.querySelector('[data-dropzone]');
        zone?.classList.add('is-filled');
        const thumb = this.querySelector('[data-thumb]');
        if (thumb) {
          thumb.src = this.photo.preview;
          thumb.hidden = false;
        }
        const prompt = this.querySelector('[data-prompt]');
        if (prompt) prompt.hidden = true;
        const clear = this.querySelector('[data-clear]');
        if (clear) clear.hidden = false;
        this.updateStatus();
        this.updateQualityWarning();
        this.hideNotice();
        this.markMissing(false);

        if (this.endpoint) this.upload(file);
        else this.syncProperties();
      }

      async upload(file) {
        const body = new FormData();
        body.append('file', file);
        body.append('frame', '1');
        body.append('product', this.dataset.product || '');
        try {
          const response = await fetch(`${this.endpoint}/upload`, { method: 'POST', body });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || `upload failed (${response.status})`);
          if (!this.photo) return;
          this.photo.url = data.url;
          this.photo.status = 'ready';
        } catch (error) {
          if (!this.photo) return;
          this.photo.status = 'error';
          this.photo.error = error.message || 'upload failed';
          console.error('photo-frame-customizer: upload failed', error);
        }
        this.updateStatus();
        this.syncProperties();
      }

      updateStatus() {
        const zone = this.querySelector('[data-dropzone]');
        if (!zone || !this.photo) return;
        zone.classList.toggle('is-uploading', this.photo.status === 'uploading');
        zone.classList.toggle('is-error', this.photo.status === 'error');
        const name = this.querySelector('[data-filename]');
        if (name) {
          if (this.photo.status === 'uploading') name.textContent = 'Uploading…';
          else if (this.photo.status === 'error') name.textContent = `Upload failed — ${this.photo.error}`;
          else name.textContent = this.photo.file.name;
        }
      }

      clearPhoto() {
        if (this.photo) URL.revokeObjectURL(this.photo.preview);
        this.photo = null;
        const art = this.wall.querySelector('[data-art="0"]');
        if (art) {
          art.style.backgroundImage = '';
          art.classList.remove('is-filled');
        }
        this.wall.querySelector('.frame')?.classList.remove('is-filled');
        const zone = this.querySelector('[data-dropzone]');
        zone?.classList.remove('is-filled', 'is-uploading', 'is-error');
        const thumb = this.querySelector('[data-thumb]');
        if (thumb) {
          thumb.removeAttribute('src');
          thumb.hidden = true;
        }
        const prompt = this.querySelector('[data-prompt]');
        if (prompt) prompt.hidden = false;
        const name = this.querySelector('[data-filename]');
        if (name) name.textContent = '';
        const clear = this.querySelector('[data-clear]');
        if (clear) clear.hidden = true;
        this.updateQualityWarning();
        this.hideNotice();
        this.markMissing(this.dataset.requirePhoto !== 'false');
        this.syncProperties();
      }

      /** Write the uploaded URL (or filename) onto the cart line item. */
      syncProperties() {
        const form = cartForm();
        if (!form) return;
        if (this.dataset.enableProperties !== 'true' && !this.endpoint) return;

        const name = 'properties[Artwork 1]';
        let input = form.querySelector(`input[name="${name}"]`);

        /*
         * Only a stored photo is worth carrying to the cart. Without an upload
         * endpoint the filename is all there is (preview-only mode); with one,
         * a photo that is still uploading or that failed has no URL yet, so it
         * is left off rather than sent as a filename the archive cannot use.
         */
        const value = this.photo ? this.photo.url || (this.endpoint ? '' : this.photo.file.name) : '';
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      /** The chosen orientation rides along on the line item for fulfilment. */
      syncOrientation() {
        const form = cartForm();
        if (!form) return;
        const name = 'properties[Orientation]';
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = this.orientation;
      }

      /** The customer's free-text customisation note rides along on the line item. */
      syncNote() {
        const form = cartForm();
        if (!form) return;
        const name = 'properties[Customisation notes]';
        const value = (this.noteEl?.value || '').trim();
        let input = form.querySelector(`input[name="${name}"]`);
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      /**
       * Warn when the uploaded photo does not have enough pixels for the
       * selected print size at 300 dpi. The frame crops with background-size:
       * cover, so the usable resolution is the smaller of width/height ratios.
       */
      updateQualityWarning() {
        if (!this.qualityEl) return;
        const photo = this.photo;
        if (!photo || !photo.width || !photo.height || !this.size) {
          this.qualityEl.hidden = true;
          this.qualityEl.textContent = "";
          return;
        }

        const { image } = this.outer();
        const dpi = Math.min(photo.width / image.w, photo.height / image.h);
        if (dpi >= 300) {
          this.qualityEl.hidden = true;
          this.qualityEl.textContent = "";
          return;
        }

        const rounded = Math.round(dpi);
        const label = `${this.size} ${this.orientation}`;
        const low = dpi < 150;
        this.qualityEl.className =
          `photo-frame-customizer__quality ${low ? "is-danger" : "is-warning"}`;
        this.qualityEl.replaceChildren();
        const heading = document.createElement("strong");
        heading.textContent = low ? "Low resolution" : "Below ideal resolution";
        const detail = document.createTextNode(
          low
            ? ` for the ${label} print: about ${rounded} dpi. It may look soft when printed. Use a larger photo.`
            : ` for the ${label} print: about ${rounded} dpi. It may look slightly soft when printed. A larger photo will look sharper.`
        );
        this.qualityEl.append(heading, detail);
        this.qualityEl.hidden = false;
      }

      /**
       * Why this order can't go through yet, or null if it can. A frame needs a
       * photo to print, so with require_photo on, an empty dropzone blocks too.
       */
      gateBlock() {
        if (!this.photo) {
          return this.dataset.requirePhoto === 'false' ? null : 'missing';
        }
        return this.photo.status === 'ready' ? null : this.photo.status;
      }

      gateSubmit() {
        if (!this.endpoint) return;
        const form = cartForm();
        if (!form) return;
        form.addEventListener(
          'submit',
          (event) => {
            const block = this.gateBlock();
            if (!block) return;

            event.preventDefault();
            event.stopImmediatePropagation();

            const notice =
              block === 'uploading'
                ? 'Hold on — your photo is still uploading. Give it a moment and try again.'
                : block === 'missing'
                  ? 'Add your photo before you order — the frame needs one to print. Then try again.'
                  : 'Your photo failed to upload. Remove and re-add it, then try again.';

            this.markMissing(block === 'missing');
            this.showNotice(notice);
            /*
             * The dropzone is in the media column, which on mobile sits below
             * the button, so send the same reason to the theme's error region
             * directly above the button.
             */
            this.showFormError(notice);
          },
          true
        );
      }

      /** Outline the empty dropzone. */
      markMissing(isMissing) {
        this.querySelector('[data-dropzone]')?.classList.toggle('is-missing', isMissing);
      }

      /**
       * Reuse the theme's own error region above the Add to cart button, the one
       * product-form.js drives, so the reason is where the button is.
       */
      showFormError(message) {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const slot = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !slot) return;
        slot.textContent = message;
        wrapper.hidden = false;
      }

      clearFormError() {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(false);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
      }

      showNotice(message) {
        const notice = this.querySelector('[data-notice]');
        if (!notice) return;
        notice.textContent = message;
        notice.hidden = false;
      }
      hideNotice() {
        const notice = this.querySelector('[data-notice]');
        if (notice) notice.hidden = true;
        this.clearFormError();
      }
    }
  );
}

// keep the customizer in step with the product's own variant picker.
// NB: the variant inputs are associated to the form via the `form` attribute,
// not by nesting, so we match on the input itself rather than a form ancestor.
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!target.matches?.('input[type="radio"][name^="Size"], input[type="radio"][name^="Color"]')) return;
    const el = document.querySelector('photo-frame-customizer');
    if (el) el.syncFromVariant();
  },
  true
);
